import { Radio, type RadioProps } from '@mantine/core';
import { useTheme } from '@contexts/ThemeContext';

/**
 * JarvisRadio - A themed Radio that uses the app theme primary/accent color.
 *
 * Mantine paints the checked fill via CSS var `--radio-color` (defaults to
 * `theme.primaryColor`). Passing `color` as a hex sets that var — nested
 * `&:checked` style overrides do not win against it.
 */
export const JarvisRadio = ({ styles, color, ...props }: RadioProps) => {
  const { colors: themeColors } = useTheme();
  const radioColor = color ?? themeColors.primary;

  return (
    <Radio
      {...props}
      color={radioColor}
      styles={(theme, styleProps, ctx) => {
        const userStyles =
          (typeof styles === 'function' ? styles(theme, styleProps, ctx) : styles) ?? {};

        return {
          ...userStyles,
          label: {
            color: themeColors.text,
            cursor: 'pointer',
            fontSize: '14px',
            ...userStyles.label,
          },
          radio: {
            cursor: 'pointer',
            borderColor: themeColors.inputBorder,
            backgroundColor: themeColors.inputBackground,
            // Checked fill reads this CSS var (not backgroundColor/:checked overrides)
            '--radio-color': radioColor,
            ...userStyles.radio,
          },
          icon: {
            color: themeColors.buttonText || themeColors.card,
            ...userStyles.icon,
          },
          description: {
            color: themeColors.textSecondary,
            fontSize: '12px',
            ...userStyles.description,
          },
          error: {
            color: themeColors.error || '#ef4444',
            fontSize: '12px',
            ...userStyles.error,
          },
        };
      }}
    />
  );
};

export default JarvisRadio;
